import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  ProductDetailsAddonIngredient,
  PurchaseProductDetailsAddonIngredient,
  PurchaseProductDetailsAddonProduct,
  PurchaseProductDetailsAddonProductVariant,
} from '@ancon/wildcat-types'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import useTranslation from 'next-translate/useTranslation'
import {
  ConfigureAddonGroupFormState,
  ConfigureAddonIngredientFormState,
  ConfigureAddonProductFormState,
} from '@ancon/wildcat-utils/product/types'
import { SelectableType } from '@ancon/wildcat-ui/web/orderweb/Selectable'
import isDetailedProductVariantOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductVariantOutOfStocks'

import ProductSelection from '../../components/ProductSelection'
import { outletSelectedOutletIdSelector } from '../../../outlet/store/outletSelector'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import getPriceLabelForVariant from '../../utils/getPriceLabelForAddonProductVariant'
import getVariantDisplayName from '../../utils/getVariantDisplayName'
import { AddonProductVariantWithVariantType } from '../../types'

import styles from './CheckBoxTypeAddonGroup.module.scss'

type CheckBoxTypeAddonGroupProps = {
  ingredients: PurchaseProductDetailsAddonIngredient[]
  addOnIndex: number
  products: PurchaseProductDetailsAddonProduct[]
}

type CheckBoxTypeAddonIngredientProps = {
  ingredient: ProductDetailsAddonIngredient
  ingredientIndex: number
  addOnIndex: number
}

type CheckBoxTypeAddonProductProps = {
  product: PurchaseProductDetailsAddonProduct
  productIndex: number
  addOnIndex: number
}

function CheckBoxAddonIngredient({
  ingredient,
  addOnIndex,
  ingredientIndex,
}: CheckBoxTypeAddonIngredientProps) {
  const freeIngredientAmountFieldName = `selectedAddons.${addOnIndex}.ingredients.${ingredientIndex}.freeAmount`
  const addOnSelectedCountFieldName = `selectedAddons.${addOnIndex}.selectedCount`
  const addOnSettingsFieldName = `selectedAddons.${addOnIndex}.addOnSettings`
  const addOnIngredientsFieldName = `selectedAddons.${addOnIndex}.ingredients`

  const { ingredientName, price, currency, id } = ingredient
  const { t } = useTranslation('common')
  const { trigger, setValue, getValues, watch } = useFormContext()
  const [freeAmount, selectedAddOnCount, addOnSettings] = watch([
    freeIngredientAmountFieldName,
    addOnSelectedCountFieldName,
    addOnSettingsFieldName,
  ])
  const addedAsFreeItem = freeAmount === 1
  const hasFreeAddons = !!addOnSettings.freeAmount
  const isWithinFreeLimit =
    hasFreeAddons && addOnSettings.freeAmount! > selectedAddOnCount
  const showFreeLabel = isWithinFreeLimit || addedAsFreeItem

  const {
    field: { value, onChange },
  } = useController({
    name: `selectedAddons.${addOnIndex}.ingredients.${ingredientIndex}.selectedCount`,
  })

  function handleCheckChange(checked: boolean) {
    onChange(checked ? 1 : 0)
    setValue(
      addOnSelectedCountFieldName,
      Math.max(checked ? selectedAddOnCount + 1 : selectedAddOnCount - 1, 0),
    )

    // Modify free addon state - Start
    // On increase add as free addOn
    if (checked && isWithinFreeLimit) {
      setValue(freeIngredientAmountFieldName, 1)
      trigger(`selectedAddons.${addOnIndex}`)
      return
    }

    // On deselect free addon assign free item to next candidate addOn item
    if (!checked && addedAsFreeItem) {
      setValue(freeIngredientAmountFieldName, 0)

      const ingredientSelectionState: ConfigureAddonIngredientFormState[] =
        getValues(addOnIngredientsFieldName)
      const nextFreeCandidateIngredientIndex =
        ingredientSelectionState.findIndex(
          ing =>
            ing.id !== id && ing.freeAmount === 0 && ing.selectedCount === 1,
        )

      if (nextFreeCandidateIngredientIndex !== -1) {
        setValue(
          `selectedAddons.${addOnIndex}.ingredients.${nextFreeCandidateIngredientIndex}.freeAmount`,
          1,
        )
      }
    }
    // Modify free addon state - End

    // Trigger Validation Cycle
    trigger(`selectedAddons.${addOnIndex}`)
  }

  return (
    <ProductSelection
      selected={value === 1}
      label={ingredientName}
      onChange={handleCheckChange}
      selectionType={SelectableType.CheckBox}
      priceLabel={
        showFreeLabel
          ? t('components.productDetailsModal.free')
          : getFormattedCurrency(
              hasFreeAddons ? addOnSettings.afterFreePrice.amount : price,
              currency,
            )
      }
      key={id}
    />
  )
}

function CheckBoxAddonProduct({
  product,
  addOnIndex,
  productIndex,
}: CheckBoxTypeAddonProductProps) {
  const freeProductAmountFieldName = `selectedAddons.${addOnIndex}.products.${productIndex}.freeAmount`
  const addOnSelectedCountFieldName = `selectedAddons.${addOnIndex}.selectedCount`
  const addOnSettingsFieldName = `selectedAddons.${addOnIndex}.addOnSettings`
  const addOnProductsFieldName = `selectedAddons.${addOnIndex}.products`
  const groupPath = `selectedAddons.${addOnIndex}`

  const { name, id } = product
  const { t } = useTranslation('common')
  const { trigger, setValue, getValues, watch } = useFormContext()
  const outletId = useAppSelector(outletSelectedOutletIdSelector)

  const [freeAmount, selectedAddOnCount, addOnSettings] = watch([
    freeProductAmountFieldName,
    addOnSelectedCountFieldName,
    addOnSettingsFieldName,
  ])

  const addedAsFreeItem = freeAmount === 1
  const hasFreeAddons = !!addOnSettings.freeAmount
  const isWithinFreeLimit =
    hasFreeAddons && addOnSettings.freeAmount! > selectedAddOnCount
  const showFreeLabel = isWithinFreeLimit || addedAsFreeItem

  // Selected count for this product
  const {
    field: { value: selectedCount, onChange: onProductCheckChange },
  } = useController({
    name: `selectedAddons.${addOnIndex}.products.${productIndex}.selectedCount`,
    defaultValue: 0,
  })

  // Selected variant (persisted in form)
  const {
    field: { value: selectedVariantId, onChange: onVariantChange },
  } = useController({
    name: `selectedAddons.${addOnIndex}.products.${productIndex}.selectedVariantId`,
    defaultValue: product.variants?.[0]?.id ?? null,
  })

  // Resolve the currently selected variant object for price label
  const selectedVariant: PurchaseProductDetailsAddonProductVariant | undefined =
    product.variants?.find(v => v.id === selectedVariantId) ??
    product.variants?.[0]

  function handleCheckChange(checked: boolean) {
    // toggle product selection 0/1
    onProductCheckChange(checked ? 1 : 0)

    // update group selected count
    setValue(
      addOnSelectedCountFieldName,
      Math.max(checked ? selectedAddOnCount + 1 : selectedAddOnCount - 1, 0),
    )

    // Free-state management — Start
    if (checked && isWithinFreeLimit) {
      // mark this product as the free item if we're still within free limit
      setValue(freeProductAmountFieldName, 1)
      trigger(groupPath)
      return
    }

    if (!checked && addedAsFreeItem) {
      // remove free flag from this product and move it to next candidate
      setValue(freeProductAmountFieldName, 0)

      const productSelectionState: ConfigureAddonProductFormState[] = getValues(
        addOnProductsFieldName,
      )

      const nextFreeCandidateProductIndex = productSelectionState.findIndex(
        p => p.id !== id && p.freeAmount === 0 && p.selectedCount === 1, // already selected
      )

      if (nextFreeCandidateProductIndex !== -1) {
        setValue(
          `selectedAddons.${addOnIndex}.products.${nextFreeCandidateProductIndex}.freeAmount`,
          1,
        )
      }
    }
    // Free-state management — End

    // Trigger validation
    trigger(groupPath)
  }

  function isProductOutOfStock() {
    if (product.hasVariants && product.variants?.length) {
      return product.variants.every(variant =>
        isDetailedProductVariantOutOfStock(variant, outletId!),
      )
    }
    return false
  }

  function priceLabelForProduct() {
    if (product.isBlocked) {
      return t('components.productDetailsModal.unavailable')
    }

    return showFreeLabel
      ? t('components.productDetailsModal.free')
      : getFormattedCurrency(
          hasFreeAddons
            ? addOnSettings.afterFreePrice.amount
            : (selectedVariant?.price.unitPrice ?? 0),
          selectedVariant?.price.unitPriceCurrency,
        )
  }

  // Price label for the product checkbox (uses selected variant)
  const productPriceLabel = priceLabelForProduct()

  const isProductSelected = selectedCount === 1

  return (
    <>
      <ProductSelection
        selected={isProductSelected}
        label={name}
        disabled={isProductOutOfStock() || product.isBlocked}
        onChange={handleCheckChange}
        selectionType={SelectableType.CheckBox}
        priceLabel={productPriceLabel}
        key={id}
      />

      {isProductSelected &&
        product.variants.map((variant: AddonProductVariantWithVariantType) => {
          const isOutOfStock = isDetailedProductVariantOutOfStock(
            variant,
            outletId!,
          )

          const variantPriceLabel = getPriceLabelForVariant(
            variant,
            showFreeLabel,
            hasFreeAddons,
            addOnSettings.afterFreePrice.amount,
            product.isBlocked,
            isOutOfStock,
            t,
          )

          return (
            <div className={styles.variantContainer} key={variant.id}>
              <ProductSelection
                label={getVariantDisplayName(
                  variant.name,
                  variant.variantType,
                  t,
                )}
                priceLabel={variantPriceLabel}
                disabled={
                  selectedCount !== 1 || isOutOfStock || product.isBlocked
                }
                selectionType={SelectableType.RadioButton}
                selected={selectedVariantId === variant.id}
                onChange={() => onVariantChange(variant.id)}
              />
            </div>
          )
        })}
    </>
  )
}

export default function CheckBoxTypeAddonGroup({
  ingredients,
  addOnIndex,
  products,
}: CheckBoxTypeAddonGroupProps) {
  const { trigger, watch } = useFormContext()
  const { t } = useTranslation('common')
  const [addOnSettings] = watch([`selectedAddons.${addOnIndex}.addOnSettings`])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const field = useController({
    name: `selectedAddons.${addOnIndex}`,
    rules: {
      validate: (value: ConfigureAddonGroupFormState) => {
        const { maximumAmount, minimumAmount } = addOnSettings
        const { selectedCount: totalAddonCount } = value

        if (minimumAmount && totalAddonCount === 0) {
          return t('components.productDetailsModal.mandatoryChoice').toString()
        }
        if (maximumAmount && totalAddonCount > maximumAmount) {
          return t('components.productDetailsModal.maxAddons', {
            maximum: maximumAmount,
          }).toString()
        }
        if (minimumAmount && totalAddonCount < minimumAmount) {
          return t('components.productDetailsModal.atLeastAddons', {
            minimum: minimumAmount,
          }).toString()
        }
        return true
      },
    },
  })

  useEffect(() => {
    // Trigger Initial Validation Cycle
    trigger(`selectedAddons.${addOnIndex}`)
  }, [trigger, addOnIndex])

  return (
    <>
      {ingredients.map((ingredient, i) => (
        <CheckBoxAddonIngredient
          ingredient={ingredient}
          ingredientIndex={i}
          addOnIndex={addOnIndex}
          key={ingredient.id}
        />
      ))}

      {products.map((product, i) => (
        <CheckBoxAddonProduct
          product={product}
          productIndex={i}
          addOnIndex={addOnIndex}
          key={product.id}
        />
      ))}
    </>
  )
}
